import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PipelineStage } from '@agency/contracts';
import { Badge, Button, EmptyState, SelectField, Skeleton, TextField, useToast } from '@agency/ui';
import { ApiClientError, get, patch, post } from '../../lib/api.ts';
import { ConflictNotice, QueryError } from '../../lib/query-state.tsx';

function StageRow({ stage }: { stage: PipelineStage }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState(stage.name);
  const [position, setPosition] = useState(String(stage.position));
  const [active, setActive] = useState(stage.active);
  const [conflict, setConflict] = useState<string | null>(null);
  useEffect(() => { setName(stage.name); setPosition(String(stage.position)); setActive(stage.active); setConflict(null); }, [stage]);
  const save = useMutation<PipelineStage, ApiClientError>({
    mutationFn: () => patch(`/pipeline/stages/${stage.id}`, { expectedVersion: stage.version, name: name.trim(), position: Number(position), active }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['pipeline-stages'] }); void qc.invalidateQueries({ queryKey: ['deals'] }); toast({ tone: 'success', message: 'Stage updated.' }); },
    onError: (error) => { if (error.code === 'VERSION_CONFLICT') setConflict(error.message); else toast({ tone: 'error', message: error.message }); },
  });
  const valid = !!name.trim() && position !== '' && Number.isInteger(Number(position)) && Number(position) >= 0 && Number(position) <= 10000;
  return (
    <section className="panel" aria-label={`${stage.name} stage`}>
      <div className="pagehead" style={{ marginBottom: 16 }}><h2>{stage.name}</h2><Badge tone={stage.active ? 'success' : 'neutral'}>{stage.active ? 'Active' : 'Inactive'}</Badge></div>
      {conflict ? <ConflictNotice message={conflict} onReload={() => void qc.invalidateQueries({ queryKey: ['pipeline-stages'] })} /> : null}
      <div className="stage-fields">
        <TextField label="Stage name" value={name} maxLength={80} onChange={(event) => setName(event.target.value)} />
        <TextField label="Order" type="number" min={0} max={10000} step={1} value={position} onChange={(event) => setPosition(event.target.value)} />
        <SelectField label="Availability" value={active ? 'active' : 'inactive'} disabled={stage.isEntry} onChange={(event) => setActive(event.target.value === 'active')}>
          <option value="active">Active</option><option value="inactive">Inactive</option>
        </SelectField>
        <Button loading={save.isPending} disabled={!valid} onClick={() => save.mutate()}>Save stage</Button>
      </div>
      {stage.isEntry ? <p className="ag-muted" style={{ marginTop: 12 }}>The entry stage stays active for new proposals.</p> : null}
    </section>
  );
}

export function PipelineStagesPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState('');
  const stages = useQuery<PipelineStage[], ApiClientError>({ queryKey: ['pipeline-stages'], queryFn: () => get('/pipeline/stages') });
  const create = useMutation<PipelineStage, ApiClientError>({
    mutationFn: () => post('/pipeline/stages', { name: name.trim() }),
    onSuccess: () => { setName(''); void qc.invalidateQueries({ queryKey: ['pipeline-stages'] }); toast({ tone: 'success', message: 'Stage added.' }); },
    onError: (error) => toast({ tone: 'error', message: error.message }),
  });
  return (
    <>
      <div className="pagehead"><div><h1>Open deal stages</h1><p>Configure steps between proposal and a final Won or Lost outcome. Order controls their display in deal views.</p></div><Link to="/deals" className="ag-btn ag-btn--secondary">Back to deals</Link></div>
      <section className="panel" style={{ marginBottom: 20 }}>
        <h2>Add a stage</h2>
        <div className="stage-fields" style={{ marginTop: 16 }}><TextField label="New stage name" value={name} maxLength={80} onChange={(event) => setName(event.target.value)} placeholder="Commercial review" /><Button variant="primary" loading={create.isPending} disabled={!name.trim()} onClick={() => create.mutate()}>Add stage</Button></div>
      </section>
      {stages.isLoading ? <div role="status"><span className="ag-visually-hidden">Loading stages…</span><Skeleton height={180} /></div> : stages.isError ? <QueryError error={stages.error} onRetry={() => void stages.refetch()} /> : stages.data?.length ? <div className="stage-list">{stages.data.map((stage) => <StageRow key={stage.id} stage={stage} />)}</div> : <EmptyState title="No stages configured">Add an entry stage before creating proposals.</EmptyState>}
    </>
  );
}
